import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type { Color } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import {
  IOsAppFeature,
  NotIOsAppFeature,
} from '@sorare/core/src/components/BuyableFeature';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useLoggedCallback } from '@sorare/core/src/hooks/useLoggedCallback';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { mobileApp } from '@sorare/core/src/lib/glossary';

import { useBestBidBelongsToUser } from '#src/hooks/auctions/useBestBidBelongsToUser.ts';
import { usePollAuction } from '#src/hooks/auctions/usePollAuction.ts';
import { useClickBidEvent } from '#src/hooks/events/useClickBidEvent.ts';
import { useCannotTrade } from '#src/hooks/offers/useCannotTrade.ts';
import { auctionMinNextBid } from '#src/lib/auctions.ts';

import type { BidField_auction } from './__generated__/index.graphql.ts';

type BidFieldProps = {
  auction: BidField_auction;
  variant: 'small' | 'medium';
  color?: Color;
};

const messages = defineMessages({
  increaseBid: {
    defaultMessage: 'Increase bid',
  },
  placeBid: {
    defaultMessage: 'Place bid',
  },
});

const Root = styled(Vertical).attrs({ gap: 0, center: true })`
  justify-content: space-between;
  overflow: visible;
  flex-grow: 1;
`;
const ActivateButton = styled(LoadingButton)`
  flex-shrink: 0;
  width: 100%;
`;
const ActivateButtonTooltip = styled(Tooltip)`
  width: 100%;
`;

export const BidField = ({
  auction,
  variant,
  color = 'primary',
}: BidFieldProps) => {
  const {
    isMarketAuthorizedIosApp,
    postMessage,
    canOpenCartOpenPaymentBoxAndCloseCurrentWebview,
  } = useIsMobileApp();
  const canOpenPaymentBox =
    isMarketAuthorizedIosApp && canOpenCartOpenPaymentBoxAndCloseCurrentWebview;
  const { toMonetaryAmountInReferenceCurrency } = useMonetaryAmount();
  usePollAuction(auction);
  const { open, cancelled } = auction;
  const trackClickBid = useClickBidEvent();
  const cannotTrade = useCannotTrade();
  const cannotTradeToken = cannotTrade();
  const openDialog = useOpenDialog();

  const auctionId = useDialogParam(DialogKey.auction);

  const { formatMessage } = useIntlContext();
  const doesBestBidBelongsToUser = useBestBidBelongsToUser();

  const bestBidBelongsToUser =
    auction?.bestBid && doesBestBidBelongsToUser(auction.bestBid);

  const loggedTogglePaymentStarted = useLoggedCallback<boolean>(() => {
    if (canOpenPaymentBox) {
      postMessage('openPaymentBox');
    }
    openDialog(DialogKey.auction, auction.id);
  });

  if (!open || cancelled) return null;

  const minNextBid = auctionMinNextBid(auction);

  const minNextBidMonetary = toMonetaryAmountInReferenceCurrency(
    minNextBid,
    auction.currency
  );

  const onClick = () => {
    trackClickBid(auction, minNextBidMonetary);
    return loggedTogglePaymentStarted(true);
  };

  return (
    <Root>
      <ActivateButtonTooltip
        title={cannotTradeToken ? formatMessage(cannotTradeToken) : ''}
      >
        <NotIOsAppFeature exceptMarketAuthorizedIosApp>
          <ActivateButton
            color={color}
            size={variant}
            disabled={Boolean(cannotTradeToken)}
            onClick={onClick}
            loading={auctionId === auction.id}
          >
            {formatMessage(
              bestBidBelongsToUser || auction.myLastBid
                ? messages.increaseBid
                : messages.placeBid
            )}
          </ActivateButton>
        </NotIOsAppFeature>
        <IOsAppFeature exceptMarketAuthorizedIosApp>
          <ActivateButton
            color={color}
            size={variant}
            disabled
            onClick={() => {}}
            loading={false}
          >
            <FormattedMessage {...mobileApp.unavailable} />
          </ActivateButton>
        </IOsAppFeature>
      </ActivateButtonTooltip>
    </Root>
  );
};

BidField.fragments = {
  auction: gql`
    fragment BidField_auction on TokenAuction {
      id
      open
      cancelled
      currency
      myLastBid {
        id
      }
      bestBid {
        id
        bidder: userBidder {
          slug
        }
        ...UseBestBidBelongsToUser_bestBid
      }
      ...UsePollAuction_auction
      ...useClickBidEvent_tokenAuction
      ...auctionMinNextBid_auction
    }
    ${useBestBidBelongsToUser.fragments.bestBid}
    ${usePollAuction.fragments.auction}
    ${useClickBidEvent.fragments.tokenAuction}
    ${auctionMinNextBid.fragments.auction}
  ` as TypedDocumentNode<BidField_auction>,
};
